import { supabase } from './supabaseClient';
import { User } from '@supabase/supabase-js';

export const getTrainingPlans = async (user: User) => {
  const { data, error } = await supabase
    .from('training_plans')
    .select('*')
    .eq('user_id', user.id);

  if (error) throw error;
  return data;
};

export const createTrainingPlan = async (user: User, plan: { name: string; description: string; start_date: string; end_date: string }) => {
  const { data, error } = await supabase
    .from('training_plans')
    .insert([{ ...plan, user_id: user.id }]);

  if (error) throw error;
  return data;
};

export const updateTrainingPlan = async (user: User, id: string, updates: Partial<{ name: string; description: string; start_date: string; end_date: string }>) => {
  const { data, error } = await supabase
    .from('training_plans')
    .update(updates)
    .eq('id', id)
    .eq('user_id', user.id);

  if (error) throw error;
  return data;
};

export const deleteTrainingPlan = async (user: User, id: string) => {
  const { error } = await supabase
    .from('training_plans')
    .delete()
    .eq('id', id)
    .eq('user_id', user.id);

  if (error) throw error;
};

export const getWorkouts = async (user: User, planId: string) => {
  const { data, error } = await supabase
    .from('workouts')
    .select('*')
    .eq('training_plan_id', planId)
    .eq('user_id', user.id);

  if (error) throw error;
  return data;
};

export const createWorkout = async (user: User, workout: { training_plan_id: string; type: string; date: string; duration_minutes: number; distance_km: number; notes: string; completed: boolean }) => {
  const { data, error } = await supabase
    .from('workouts')
    .insert([{ ...workout, user_id: user.id }]);

  if (error) throw error;
  return data;
};

export const updateWorkout = async (user: User, id: string, updates: Partial<{ type: string; date: string; duration_minutes: number; distance_km: number; notes: string; completed: boolean }>) => {
  const { data, error } = await supabase
    .from('workouts')
    .update(updates)
    .eq('id', id)
    .eq('user_id', user.id);

  if (error) throw error;
  return data;
};

export const deleteWorkout = async (user: User, id: string) => {
  const { error } = await supabase
    .from('workouts')
    .delete()
    .eq('id', id)
    .eq('user_id', user.id);

  if (error) throw error;
};

export const saveGeneratedPlan = async (
  user: User,
  plan: { name: string; description: string; start_date: string; end_date: string },
  sessions: {
    training_plan_id: string;
    week_number: number;
    day_of_week: number;
    scheduled_date: string;
    session_name: string;
    discipline: string;
    category: string;
    planned_duration_minutes: number;
    target_zone_type: string;
    target_zone_value: number;
    has_run_walk: boolean;
    run_walk_ratio: string | null;
    run_walk_description: string | null;
    is_brick: boolean;
    brick_run_zone: string | null;
    brick_run_duration: number | null;
    customizations: any;
    targets: any;
  }[]
) => {
  const { data: planData, error: planError } = await supabase
    .from('training_plans')
    .insert([{ ...plan, user_id: user.id }])
    .select()
    .single();

  if (planError) throw planError;

  const sessionsWithPlanId = sessions.map(s => ({
    ...s,
    training_plan_id: planData.id,
    user_id: user.id,
  }));

  const { data: sessionData, error: sessionError } = await supabase
    .from('plan_sessions')
    .insert(sessionsWithPlanId)
    .select();

  if (sessionError) throw sessionError;

  return { plan: planData, sessions: sessionData };
};

export const getPlanSessions = async (user: User, planId: string) => {
  const { data, error } = await supabase
    .from('plan_sessions')
    .select('*')
    .eq('training_plan_id', planId)
    .eq('user_id', user.id)
    .order('week_number', { ascending: true })
    .order('day_of_week', { ascending: true });

  if (error) throw error;
  return data;
};

export const updatePlanSession = async (
  user: User,
  id: string,
  updates: Partial<{
    day_of_week: number;
    scheduled_date: string;
    planned_duration_minutes: number;
    target_zone_type: string;
    target_zone_value: number;
    has_run_walk: boolean;
    run_walk_ratio: string | null;
    run_walk_description: string | null;
    customizations: any;
    targets: any;
  }>
) => {
  const { data, error } = await supabase
    .from('plan_sessions')
    .update(updates)
    .eq('id', id)
    .eq('user_id', user.id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const movePlanSession = async (
  user: User,
  sessionId: string,
  newDay: number,
  newDate: string
) => {
  const { data, error } = await supabase
    .from('plan_sessions')
    .update({
      day_of_week: newDay,
      scheduled_date: newDate,
    })
    .eq('id', sessionId)
    .eq('user_id', user.id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const validateSessionMove = async (
  user: User,
  sessionId: string,
  targetDay: number,
  planId: string
) => {
  const { data: allSessions, error: fetchError } = await supabase
    .from('plan_sessions')
    .select('*')
    .eq('training_plan_id', planId)
    .eq('user_id', user.id);

  if (fetchError) throw fetchError;

  const movingSession = allSessions.find((s: any) => s.id === sessionId);
  if (!movingSession) {
    return { valid: false, warnings: [{ severity: 'error', message: 'Session not found', suggestion: '' }] };
  }

  const otherSessions = allSessions.filter((s: any) => s.id !== sessionId);
  const sessionsOnTargetDay = otherSessions.filter((s: any) => s.day_of_week === targetDay);

  const warnings: { severity: string; message: string; suggestion: string }[] = [];

  const targetDayHasLongBike = sessionsOnTargetDay.some(
    (s: any) => s.discipline === 'bike' && s.planned_duration_minutes >= 90
  );

  if (movingSession.is_long_session && targetDayHasLongBike) {
    warnings.push({
      severity: 'error',
      message: 'Long runs should typically be on fresh legs, not after long bikes.',
      suggestion: 'Move long run to a different day.',
    });
  }

  const hardCategories = ['tempo', 'threshold', 'interval', 'brick'];
  const hardZones = ['T', 'I', 'Z3', 'Z4', 'Z5', 'CSS-X'];
  const isHardSession =
    hardCategories.includes(movingSession.category) || hardZones.includes(movingSession.target_zone_type);

  if (isHardSession) {
    const prevDay = targetDay === 1 ? 7 : targetDay - 1;
    const prevDayHasLong = otherSessions.some(
      (s: any) => s.day_of_week === prevDay && (s.planned_duration_minutes >= 90 || s.is_long_session)
    );

    if (prevDayHasLong) {
      warnings.push({
        severity: 'warning',
        message: 'Hard sessions after long sessions increase injury risk.',
        suggestion: 'Consider moving to a day after a rest day.',
      });
    }

    const nextDay = targetDay === 7 ? 1 : targetDay + 1;
    const nextDayHasHard = otherSessions.some(
      (s: any) => s.day_of_week === nextDay && hardCategories.includes(s.category)
    );

    if (nextDayHasHard) {
      warnings.push({
        severity: 'warning',
        message: 'This creates consecutive hard days. Cumulative fatigue may build up.',
        suggestion: 'Consider adding a recovery day between hard sessions.',
      });
    }
  }

  return {
    valid: !warnings.some(w => w.severity === 'error'),
    warnings,
  };
};

export const regenerateWeekSessions = async (
  user: User,
  planId: string,
  weekNumber: number,
  raceType: string,
  experienceLevel: string,
  sessionsPerWeek: number,
  preferredDays: number[],
  athleteSettingsId: string
) => {
  const { data: allSessions, error: fetchError } = await supabase
    .from('plan_sessions')
    .select('*')
    .eq('training_plan_id', planId)
    .eq('user_id', user.id);

  if (fetchError) throw fetchError;

  const existingWeekStart = allSessions.find((s: any) => s.week_number === weekNumber)?.scheduled_date;

  const newSessions: any[] = [];
  const availableDays = [...preferredDays].sort((a, b) => a - b);

  let sessionIndex = 0;
  const sessionCount = Math.min(sessionsPerWeek, availableDays.length);

  while (sessionIndex < sessionCount) {
    const dayOfWeek = availableDays[sessionIndex];
    const dayOffset = dayOfWeek - 1;
    const scheduledDate = new Date(existingWeekStart);
    scheduledDate.setDate(scheduledDate.getDate() + dayOffset);
    const scheduledDateStr = scheduledDate.toISOString().split('T')[0];

    const disciplineOptions = ['swim', 'bike', 'run'];
    const discipline = disciplineOptions[sessionIndex % 3];
    const category = sessionIndex === sessionCount - 1 ? 'endurance' : 'recovery';
    const isLong = discipline === 'run' && sessionIndex === sessionCount - 1;

    newSessions.push({
      training_plan_id: planId,
      user_id: user.id,
      week_number: weekNumber,
      day_of_week: dayOfWeek,
      scheduled_date: scheduledDateStr,
      session_name: `${category.charAt(0).toUpperCase() + category.slice(1)} ${discipline.charAt(0).toUpperCase() + discipline.slice(1)}`,
      discipline,
      category,
      planned_duration_minutes: isLong ? 90 : 60,
      target_zone_type: discipline === 'swim' ? 'CSS' : discipline === 'bike' ? 'Z2' : 'E',
      target_zone_value: 0,
      has_run_walk: discipline === 'run' && isLong,
      run_walk_ratio: discipline === 'run' && isLong ? '9:1' : null,
      run_walk_description: discipline === 'run' && isLong ? '9:1 run/walk pattern' : null,
      is_brick: false,
      is_long_session: isLong,
      brick_run_zone: null,
      brick_run_duration: null,
      customizations: {},
      targets: {},
    });

    sessionIndex++;
  }

  const { error: deleteError } = await supabase
    .from('plan_sessions')
    .delete()
    .eq('training_plan_id', planId)
    .eq('user_id', user.id)
    .eq('week_number', weekNumber);

  if (deleteError) throw deleteError;

  const { data: insertedData, error: insertError } = await supabase
    .from('plan_sessions')
    .insert(newSessions)
    .select();

  if (insertError) throw insertError;

  return insertedData;
};

export const getSessionTargets = async (user: User, sessionId: string) => {
  const { data, error } = await supabase
    .from('plan_sessions')
    .select('targets')
    .eq('id', sessionId)
    .eq('user_id', user.id)
    .single();

  if (error) throw error;
  return data?.targets;
};

export const updateSessionTargets = async (user: User, sessionId: string, targets: any) => {
  const { data, error } = await supabase
    .from('plan_sessions')
    .update({ targets })
    .eq('id', sessionId)
    .eq('user_id', user.id)
    .select()
    .single();

  if (error) throw error;
  return data;
};
